import { useEffect, useId, useState, type KeyboardEvent } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { api } from '@pc/api/client';
import type { operations } from '@pc/api/schema';
import { ApiErrorNotice } from '../ApiErrorNotice';
import { Id, Loading } from '../ui';

type ListResponse = operations['listAccounts']['responses'][200]['content']['application/json'];
type AccountRecord = ListResponse['records'][number];
type ListQuery = NonNullable<operations['listAccounts']['parameters']['query']>;

/**
 * Choosing an account without already knowing its snowflake.
 *
 * The contract offers no prefix search. `?q=` is a resolver — it "resolves to
 * at most one account instead of paging" — so it only answers once a complete
 * identifier has been typed, and cannot drive a typeahead on its own. The
 * suggestions therefore come from the paged list held in the query cache and
 * are filtered here, in the browser.
 *
 * That is the right shape for tens to hundreds of accounts, which is what this
 * platform has. Past roughly a thousand it stops being: the pages stop being
 * cheap to hold and the cap below starts hiding real accounts. The fix at that
 * point is a prefix search on the backend, not a larger cap here.
 *
 * The gap this picker cannot close on its own is named in `pickerAccountLabel`:
 * the list now carries LOCID usernames, but only for the pages already loaded.
 * That is why the dead-end message below names the fields it did search instead
 * of implying the account does not exist, and why `q` still resolves any
 * complete-looking token rather than only a snowflake or an email — it reaches
 * accounts beyond the pages held here.
 */

/**
 * The contract's own pattern for a Discord snowflake
 * (`^[1-9][0-9]{16,19}$`), used both to decide when `q` is worth sending and
 * to decide when a typed value is submittable. Exported because the picker is
 * what defines "a complete identifier" for its callers.
 */
const DISCORD_ID_PATTERN = /^[1-9][0-9]{16,19}$/;

export function isDiscordSnowflake(value: string): boolean {
  return DISCORD_ID_PATTERN.test(value);
}

/**
 * Whether `q` could conceivably answer for a term.
 *
 * `q` takes "a Discord snowflake, a cmpID, or an email/username resolved
 * through LOCID" — four shapes with no separator between them, so the only
 * thing they have in common is that each is a single token an identifier could
 * be spelled with. Testing for that, rather than for a snowflake or an email
 * specifically, is what lets a typed-out username reach the resolver at all;
 * the previous predicate excluded exactly the case an operator hits when the
 * local fields cannot match, which is how "type the whole thing" became the
 * only working technique.
 *
 * It is a filter on obvious waste, not a validation: a prefix that happens to
 * look like a token still costs a request that returns nothing. The length
 * floor and the debounce below are what keep that from being one request per
 * keystroke.
 */
const RESOLVABLE_PATTERN = /^[A-Za-z0-9._+@-]{3,255}$/;

function isResolvable(value: string): boolean {
  return RESOLVABLE_PATTERN.test(value);
}

/**
 * `limit` above 200 is capped rather than rejected, so asking for the cap costs
 * nothing and turns the usual account list into a page or two. `MAX_PAGES`
 * bounds the walk — an unbounded follow of `nextCursor` would let one focused
 * field issue an unbounded number of requests against a 120/min budget.
 *
 * Exported, alongside the cache key it feeds below, because `KnownPerson.tsx`
 * joins against this exact picker cache rather than issuing a request of its
 * own — see `accountDirectoryKey`.
 */
export const ACCOUNT_PAGE_LIMIT = 200;
const MAX_PAGES = 5;
const MAX_SUGGESTIONS = 8;

/**
 * How much has to be typed before a suggestion is worth making.
 *
 * One character over several hundred accounts suggests nothing useful and
 * pushes the real match off the end of the list; a complete identifier is what
 * the operator was trying to avoid typing. Two is where a prefix starts to
 * discriminate, and it is what was actually asked for.
 */
const MIN_TERM_LENGTH = 2;

/**
 * How long a term has to stand still before it is sent to the resolver.
 *
 * Local filtering runs on every keystroke because it costs nothing. `q` is a
 * network request against a 120/min budget, and every intermediate prefix of a
 * username is a request that cannot match — so it waits for the operator to
 * stop, which in practice makes one request per word rather than one per
 * letter.
 */
const RESOLVE_DEBOUNCE_MS = 200;

/**
 * A key of its own rather than AccountsBrowser's `['accounts', 'list', …]`.
 * The shapes look interchangeable and are not: that query pages 50 at a time
 * under whatever filters the operator set, this one pages 200 unfiltered, and
 * sharing a key would hand each the other's pages under a `pageParam` that
 * does not match them. Both sit under `['accounts']`, so provisioning's
 * invalidation still refreshes this.
 *
 * Exported so `KnownPerson.tsx`'s `useAccountDirectory` can land on this exact
 * cache entry — TanStack keys on deep equality, so it has to ask for the
 * identical array rather than one that merely looks the same. Restating
 * `['accounts', 'picker', 200]` in a second file would work today and then
 * silently fork the moment either definition changed without the other.
 */
export const accountDirectoryKey = ['accounts', 'picker', ACCOUNT_PAGE_LIMIT] as const;

export function OwnerPicker({
  id,
  value,
  onChange,
  onDraftChange,
}: {
  id: string;
  /** The chosen account's `ownerDiscordID`, or `''` when nothing is chosen. */
  value: string;
  onChange: (ownerDiscordID: string) => void;
  /**
   * The raw text in the field, whenever it changes.
   *
   * `onChange` reports a resolved snowflake or `''`, which leaves a caller
   * unable to distinguish "nothing typed" from "typed something that has not
   * resolved". A filter has to: the first means show everything, the second
   * means wait. Optional because the only caller that needs it is the one where
   * an empty value is itself a valid filter.
   */
  onDraftChange?: (draft: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const baseId = useId();
  const listboxId = `${baseId}-listbox`;
  const optionId = (index: number) => `${baseId}-option-${index}`;

  const typed = draft.trim();
  const term = typed.toLowerCase();

  const list = useInfiniteQuery({
    queryKey: accountDirectoryKey,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }): Promise<ListResponse> => {
      const { data } = await api.GET('/api/accounts', {
        params: {
          query: {
            limit: ACCOUNT_PAGE_LIMIT,
            ...(pageParam === undefined ? {} : { cursor: pageParam }),
          } satisfies ListQuery,
        },
      });
      return data as ListResponse;
    },
    // Same stop condition as the browser: the absence of a cursor, never a
    // short page.
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });

  const { data, hasNextPage, isFetchingNextPage, fetchNextPage } = list;
  const pageCount = data?.pages.length ?? 0;
  const capped = pageCount >= MAX_PAGES;

  /*
   * Driven off `pageCount`, which only ever increases, rather than off
   * `isFetchingNextPage`. When a page resolves quickly enough that React
   * batches the fetching flag's rise and fall into one render, that flag reads
   * the same before and after — the effect never re-runs and the walk silently
   * stops two pages in. A monotonic dependency cannot be coalesced away.
   */
  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage || pageCount >= MAX_PAGES) return;
    void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, pageCount, fetchNextPage]);

  /** Still walking pages, so "no match" is not yet a fact about the account. */
  const paging = list.isPending || isFetchingNextPage || (hasNextPage === true && !capped);
  const truncated = hasNextPage === true && capped;

  const loaded = data?.pages.flatMap((page) => page.records) ?? [];
  const searchable = term.length >= MIN_TERM_LENGTH;
  const matches = searchable
    ? loaded
        .filter((record) => searchableFields(record).some((field) => field.includes(term)))
        .sort(
          (a, b) =>
            rank(a, term) - rank(b, term) ||
            pickerAccountLabel(a).localeCompare(pickerAccountLabel(b)),
        )
    : [];

  /*
   * The term as the resolver sees it: whatever the operator last stopped
   * typing. Kept separate from `typed` so that the local list re-filters
   * instantly while the request waits, and so that an option resolved for a
   * term the operator has since moved past is never offered as though it
   * answered the current one.
   */
  const [settledTerm, setSettledTerm] = useState(typed);

  useEffect(() => {
    const timer = window.setTimeout(() => setSettledTerm(typed), RESOLVE_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [typed]);

  const settled = settledTerm === typed;

  /*
   * The one thing client-side filtering cannot do: find an account that is not
   * in the pages we hold, or one whose only recognisable identifier the list
   * records do not carry. Sent only once the local list has been walked and
   * come up empty, so an operator whose account is right here never spends a
   * request at all.
   */
  const resolvable = isResolvable(settledTerm);
  const resolve = useQuery({
    queryKey: ['accounts', 'resolve', settledTerm],
    queryFn: async (): Promise<ListResponse> => {
      const { data: resolved } = await api.GET('/api/accounts', {
        params: { query: { q: settledTerm } satisfies ListQuery },
      });
      return resolved as ListResponse;
    },
    enabled: resolvable && !paging && matches.length === 0,
  });

  const resolved = settled ? (resolve.data?.records ?? []) : [];
  const options = (matches.length > 0 ? matches : resolved).slice(0, MAX_SUGGESTIONS);

  const expanded = open && options.length > 0;

  const selected =
    value === ''
      ? undefined
      : [...loaded, ...(resolve.data?.records ?? [])].find(
          (record) => record.ownerDiscordID === value,
        );

  function commit(next: string) {
    setDraft(next);
    onDraftChange?.(next);
    setOpen(true);
    setActiveIndex(-1);
    /*
     * A snowflake typed or pasted straight in is submittable whether or not it
     * matched anything here. The picker is a convenience over an id the API
     * already accepts; refusing an id because our own cached pages did not
     * contain it would make the convenience a gate.
     */
    const trimmed = next.trim();
    onChange(isDiscordSnowflake(trimmed) ? trimmed : '');
  }

  function select(record: AccountRecord) {
    /*
     * The field now reads back the name the operator picked, not the snowflake
     * behind it. The snowflake is still what `onChange` reports and still what
     * gets submitted, and it is still on screen a line below — but as the
     * value to copy out, rather than as the answer to "who did I choose?". An
     * 18-digit number is the least legible confirmation this form could give
     * of a choice made by name.
     */
    const label = pickerAccountLabel(record);
    setDraft(label);
    onDraftChange?.(label);
    onChange(record.ownerDiscordID);
    setOpen(false);
    setActiveIndex(-1);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (options.length === 0) return;
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) => step(current, options.length, event.key === 'ArrowDown' ? 1 : -1));
      return;
    }

    if (event.key === 'Enter') {
      const chosen = expanded ? options[activeIndex] : undefined;
      if (!chosen) return;
      event.preventDefault();
      select(chosen);
    }
  }

  /**
   * Nothing matched, nothing is still in flight, and nothing is chosen.
   *
   * `settled` belongs in here because without it the message appears in the gap
   * between the last keystroke and the request it triggers — telling an
   * operator their account does not exist while the console is still in the act
   * of looking for it.
   */
  const deadEnd =
    searchable &&
    value === '' &&
    settled &&
    !paging &&
    !resolve.isFetching &&
    options.length === 0;

  /** Whether the resolver has actually answered for what is on screen. */
  const resolveAnswered = resolvable && resolve.isSuccess;

  return (
    <>
      <div className="picker">
        <input
          id={id}
          role="combobox"
          aria-expanded={expanded}
          aria-controls={listboxId}
          aria-autocomplete="list"
          {...(expanded && activeIndex >= 0 ? { 'aria-activedescendant': optionId(activeIndex) } : {})}
          value={draft}
          onChange={(event) => commit(event.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          placeholder="Username, CMP ID or Discord ID"
          autoComplete="off"
          spellCheck={false}
        />

        {/*
          * Always rendered so `aria-controls` resolves to a real element even
          * while empty, which is what a combobox is required to promise.
          */}
        <ul
          id={listboxId}
          role="listbox"
          aria-label="Matching accounts"
          className="picker-listbox"
          hidden={!expanded}
        >
          {expanded &&
            options.map((record, index) => (
              <li
                key={record.ownerDiscordID}
                id={optionId(index)}
                role="option"
                aria-selected={index === activeIndex}
                className="picker-option"
                // Keeps focus in the input, so the blur that closes this list
                // never fires before the click that selects from it.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => select(record)}
              >
                <Id>{pickerAccountLabel(record)}</Id>{' '}
                <span className="absent">{secondaryLabel(record)}</span>{' '}
                <Id>{record.ownerDiscordID}</Id>
              </li>
            ))}
        </ul>
      </div>

      {paging && typed.length > 0 && <Loading what="the account list" />}

      {typed.length > 0 && !searchable && (
        <p className="absent">
          Type at least {MIN_TERM_LENGTH} characters to see suggestions.
        </p>
      )}

      {matches.length > options.length && (
        <p className="absent">
          {options.length} of {matches.length} matches shown. Keep typing to narrow.
        </p>
      )}

      {truncated && (
        <p role="status">
          Suggestions cover the first {loaded.length.toLocaleString()} accounts only, and more exist
          past that. Paste a full Discord ID or an email address to look one up directly.
        </p>
      )}

      {value !== '' &&
        (selected ? (
          <p>
            Owner <Id>{pickerAccountLabel(selected)}</Id> · <Id>{selected.cmpID}</Id> ·{' '}
            <Id>{selected.ownerDiscordID}</Id>
          </p>
        ) : (
          <p>
            No account here carries that snowflake. It will be sent exactly as typed — the API
            decides whether it exists.
          </p>
        ))}

      {/*
        * Why nothing matched, in the operator's terms rather than the
        * component's. The previous wording — "No account matches" — described
        * the local filter as though it were the account list itself, so an
        * operator typing an identifier beyond the pages already loaded read a
        * fact about our cache as a fact about the person.
        */}
      {deadEnd && (
        <p role="alert">
          Nothing here matches “{typed}”. Suggestions are filtered over the only identifiers a
          list record carries — {SEARCHED_FIELD_NAMES}.{' '}
          {resolveAnswered
            ? 'The API was asked to resolve it in full as well, and returned no account.'
            : 'A complete username, email address or Discord ID is resolved by the API instead, so typing the whole one will still find it.'}
        </p>
      )}

      {/*
        * A failed list read costs the suggestions, not the field: a snowflake
        * pasted in still provisions. Same reasoning as the quota check in
        * CreateContainer — an inability to look something up is not a refusal.
        */}
      {list.isError && (
        <>
          <p>Accounts could not be listed, so there are no suggestions. Paste a Discord ID instead.</p>
          <ApiErrorNotice error={list.error} onRetry={() => void list.refetch()} />
        </>
      )}

      {resolve.isError && <ApiErrorNotice error={resolve.error} onRetry={() => void resolve.refetch()} />}
    </>
  );
}

/**
 * The most human identifier a list record carries.
 *
 * LOCID username leads, then the directory display name. `shellUsername` is
 * the name of a *shell account*: a different thing that coincides with the
 * username often enough to be useful and not often enough to be relied on.
 * The CMP ID remains the last fallback, so every screen that leads with an
 * account goes through one ordering rather than drifting apart.
 */
export function accountLabel(record: AccountRecord): string {
  return record.owner?.username ?? record.owner?.displayName ?? record.shellUsername ?? record.cmpID;
}

/**
 * The picker's own label — displayed in the listbox, written into the field
 * on selection, and shown beside the chosen owner below.
 *
 * This is the one control whose job is turning a remembered human back into
 * an id, so it leads with the directory display name rather than the LOCID
 * username `accountLabel` prefers everywhere else: a username answers
 * `matthew`, never `Matthew Ray`. Kept as a separate function rather than
 * reordering `accountLabel` itself, which `AccountsBrowser` also calls
 * directly to render its own rows — this is scoped to the picker alone, and
 * that screen's ordering is untouched.
 */
function pickerAccountLabel(record: AccountRecord): string {
  return record.owner?.displayName ?? record.owner?.username ?? record.shellUsername ?? record.cmpID;
}

/**
 * Every identifier on a list record an operator might type, most-likely first.
 *
 * The order is the ranking's tiebreak as well as the filter's, so it is the
 * order of intent: LOCID username, display name and email are what someone
 * reaches for first; a shell username is next, a cmpID is what they paste from
 * a ticket, the Warpgate pair is what they read off a session log, and the
 * snowflake is what they have when they have nothing else.
 *
 * `tier` and `active` are deliberately absent. They are the two fields on the
 * record that are not identifiers — facets with a handful of values each, where
 * a term short enough to type would match most of the list and a term long
 * enough to discriminate does not exist. The API filters on both directly
 * (`?tier=`, `?active=`), which is the honest way to ask that question.
 *
 * The owner fields lead because the list now carries them, and the array order
 * also decides ties between otherwise equally good matches. See `accountLabel`.
 */
function searchableFields(record: AccountRecord): string[] {
  return [
    record.owner?.username ?? null,
    record.owner?.displayName ?? null,
    record.owner?.email ?? null,
    record.shellUsername,
    record.cmpID,
    record.warpgateUserId,
    record.warpgateRoleName,
    record.ownerDiscordID,
  ]
    /*
     * `typeof` rather than `!== null`, even though every field above is typed
     * `string | null`. The predicate asserts `field is string` and the compiler
     * takes its word for it, so a field the API omitted arrives as `undefined`,
     * passes a null check, and dies on `.toLowerCase()` — which happens inside
     * a keystroke handler, so the whole console goes to the error boundary
     * because somebody typed in a search box. The contract does mark all of
     * these required; this is the assertion paying for itself if that ever
     * stops being true.
     */
    .filter((field): field is string => typeof field === 'string' && field !== '')
    .map((field) => field.toLowerCase());
}

/**
 * Named in the dead-end message, so the copy cannot claim to search a field
 * that `searchableFields` does not.
 */
const SEARCHED_FIELD_NAMES =
  'LOCID username, display name, email address, shell username, CMP ID, Warpgate user, Warpgate role and Discord ID';

/**
 * How well a record answers the term, lowest first.
 *
 * Exact beats prefix and prefix beats substring, because a term is a prefix far
 * more often than it is a fragment — typing `mr` means `mray`, not `dmr-ops`,
 * and the old two-way split put those in whichever order the API happened to
 * return them. A prefix of the *label* outranks a prefix of any other field for
 * the same reason: the thing the operator can see in the list is the thing they
 * were most likely typing.
 */
function rank(record: AccountRecord, term: string): number {
  const fields = searchableFields(record);
  if (fields.some((field) => field === term)) return 0;
  if (pickerAccountLabel(record).toLowerCase().startsWith(term)) return 1;
  if (fields.some((field) => field.startsWith(term))) return 2;
  return 3;
}

/**
 * The identifier that is *not* leading the option, so the row carries two
 * different handles rather than the same one twice. When there is no shell
 * username the cmpID has already been promoted to the label, and what is worth
 * saying instead is why.
 */
function secondaryLabel(record: AccountRecord): string {
  return record.shellUsername ? record.cmpID : 'no shell user';
}

/** Wraps, and treats "nothing active" as just before the first option. */
function step(current: number, length: number, delta: number): number {
  if (current < 0) return delta > 0 ? 0 : length - 1;
  return (current + delta + length) % length;
}
